import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
  Pagination, Stack, CircularProgress, IconButton, Tooltip, Avatar, Button,
  Alert,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { getImageUrl } from '../config';
import { color } from '../theme/tokens';
import {
  PageHeader, Toolbar, Panel, DataTable, StatusBadge, FormDialog, useFeedback,
} from '../components/ui';

// ── Status config ──────────────────────────────────────────────
// Numeric values are fixed by the API: -1 All, 0 Pending, 1 Placed,
// 2 Processing, 3 Shipped, 4 Delivered, 5 Cancelled.
const ORDER_STATUSES = [
  { value: -1, label: 'All Orders' },
  { value:  0, label: 'Pending' },
  { value:  1, label: 'Placed' },
  { value:  2, label: 'Processing' },
  { value:  3, label: 'Shipped' },
  { value:  4, label: 'Delivered' },
  { value:  5, label: 'Cancelled' },
];

const STATUS_LABEL = ORDER_STATUSES.reduce((acc, s) => {
  acc[s.value] = s.label;
  return acc;
}, {});

const STATUS_TONE = {
  0: 'warning',
  1: 'info',
  2: 'warning',
  3: 'goldTone',
  4: 'success',
  5: 'danger',
};

const fmt = (n) =>
  n != null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const labelValueSx = {
  label: { fontSize: '0.75rem', color: color.ink[500], display: 'block' },
  value: { fontSize: '0.875rem', fontWeight: 500, color: color.ink[900], wordBreak: 'break-word' },
};

// ── Order detail dialog ────────────────────────────────────────
const OrderDetailDialog = ({ orderId, open, onClose, onChanged }) => {
  const { success, error } = useFeedback();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [assigning, setAssigning] = useState(false);

  const loadCandidates = useCallback(async (id) => {
    if (!id) return;
    setCandidatesLoading(true);
    try {
      const r = await api.get(`/api/admin/orders/${id}/candidates`);
      setCandidates(r.data.data.candidates || []);
    } catch {
      setCandidates([]);
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    setLoadError('');
    setSelectedCandidate('');
    api.get(`/api/admin/orders/${orderId}`)
      .then(r => {
        setDetail(r.data.data);
        setNewStatus(r.data.data.order.ORDER_STATUS);
      })
      .catch(() => setLoadError('Failed to load order detail'))
      .finally(() => setLoading(false));
    loadCandidates(orderId);
  }, [open, orderId, loadCandidates]);

  const handleStatusSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/admin/orders/${orderId}/status`, { status: newStatus });
      success('Status updated successfully');
      onChanged();
    } catch {
      error('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // branchCode omitted = re-run the automatic pick
  const handleAssign = async (branchCode) => {
    setAssigning(true);
    try {
      await api.put(`/api/admin/orders/${orderId}/assign-store`, branchCode ? { branchCode } : {});
      const r = await api.get(`/api/admin/orders/${orderId}`);
      setDetail(r.data.data);
      setSelectedCandidate('');
      await loadCandidates(orderId);
      success(branchCode ? 'Store assigned' : 'Store re-selected automatically');
      onChanged();
    } catch (e) {
      error(e?.response?.data?.message || 'Failed to assign store');
    } finally {
      setAssigning(false);
    }
  };

  const summary = detail ? [
    ['Customer', detail.order.CUSTOMER_NAME || detail.order.EMAIL],
    ['Mobile', detail.order.CUSTOMER_MOBILE || '—'],
    ['Email', detail.order.EMAIL],
    ['Amount Paid', fmt(detail.order.AMOUNT_PAID)],
    ['Payment', detail.order.PAYMENT_STATUS],
    ['Date', fmtDate(detail.order.order_date)],
  ] : [];

  // ── Fulfilment store (which shop actually sends the order) ──
  const o = detail?.order;
  const hasStore = !!o?.ASSIGNED_STORE_CODE;
  const coverageTone = o?.COVERAGE === 'full' ? 'success' : o?.COVERAGE === 'partial' ? 'warning' : 'neutral';
  const coverageLabel = o?.COVERAGE === 'full' ? 'All items in stock'
    : o?.COVERAGE === 'partial' ? 'Partial stock' : 'Unassigned';

  const candidateLabel = (c) =>
    `${c.store_name || c.store_code} · ${c.items_covered}/${c.items_total} items` +
    (c.distance_km != null ? ` · ${c.distance_km} km` : '') +
    (c.units_available != null ? ` · ${c.units_available} units` : '');

  const itemColumns = [
    {
      key: 'ItemName',
      label: 'Product',
      render: (_v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={getImageUrl(row.image)}
            variant="rounded"
            sx={{ width: 40, height: 40 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: color.ink[900] }}>
              {row.ItemName || row.ITEMCODE}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
              {[row.Brand, row.MODEL].filter(Boolean).join(' ')}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { key: 'UNIT', label: 'Qty', align: 'right' },
    { key: 'SalePrice', label: 'Price', align: 'right', render: (v) => fmt(v) },
    { key: 'LineTotal', label: 'Total', align: 'right', render: (v) => fmt(v) },
  ];

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={`Order #${orderId}`}
      maxWidth="md"
      error={loadError}
      actions={<Button onClick={onClose} variant="outlined" color="inherit">Close</Button>}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : detail && (
        <>
          {/* Order summary */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mt: 1,
              mb: 2.5,
            }}
          >
            {summary.map(([label, value]) => (
              <Box key={label}>
                <Typography sx={labelValueSx.label}>{label}</Typography>
                <Typography sx={labelValueSx.value}>{value}</Typography>
              </Box>
            ))}
            {detail.order.DELIVERY_ADDRESS && (
              <Box sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}>
                <Typography sx={labelValueSx.label}>Delivery Address</Typography>
                <Typography sx={labelValueSx.value}>{detail.order.DELIVERY_ADDRESS}</Typography>
              </Box>
            )}
          </Box>

          {/* Fulfilment store */}
          <Typography sx={{ mb: 1, fontSize: '0.8125rem', fontWeight: 600, color: color.ink[700] }}>
            Fulfilment store
          </Typography>
          <Box
            sx={{
              border: `1px solid ${hasStore ? color.divider : color.goldTone.border}`,
              borderRadius: '12px',
              p: 2,
              mb: 3,
              background: hasStore ? color.surfaceAlt : color.goldTone.bg,
            }}
          >
            {hasStore ? (
              <>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: color.ink[900] }}>
                    {o.ASSIGNED_STORE_NAME || o.ASSIGNED_STORE_CODE}
                  </Typography>
                  <StatusBadge label={coverageLabel} tone={coverageTone} />
                  {o.ASSIGNMENT_MODE === 'manual' && <StatusBadge label="Manual" tone="info" />}
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(150px, 1fr))' },
                    gap: 2,
                  }}
                >
                  {[
                    ['Branch code', o.ASSIGNED_STORE_CODE],
                    ['City', o.ASSIGNED_STORE_CITY || '—'],
                    ['Distance', o.ASSIGNED_DISTANCE_KM != null ? `${o.ASSIGNED_DISTANCE_KM} km` : '—'],
                    ['Items held', `${o.ASSIGNED_ITEMS_COVERED ?? 0} of ${o.ASSIGNED_ITEMS_TOTAL ?? 0}`],
                    ['Units available', o.ASSIGNED_UNITS ?? 0],
                    ['Delivery PIN', o.DELIVERY_PIN || '—'],
                    ['Assigned', `${o.ASSIGNMENT_MODE === 'manual' ? 'Manually' : 'Automatically'} · ${fmtDate(o.ASSIGNED_AT)}`],
                  ].map(([label, value]) => (
                    <Box key={label}>
                      <Typography sx={labelValueSx.label}>{label}</Typography>
                      <Typography sx={labelValueSx.value}>{value}</Typography>
                    </Box>
                  ))}
                </Box>

                {o.COVERAGE === 'partial' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    This shop only holds {o.ASSIGNED_ITEMS_COVERED} of {o.ASSIGNED_ITEMS_TOTAL} items on this
                    order — the rest has to come from elsewhere.
                  </Alert>
                )}
                {o.ASSIGNED_DISTANCE_KM == null && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Distance unknown — the delivery pincode has no coordinates, so this shop was picked on
                    stock alone.
                  </Alert>
                )}
              </>
            ) : (
              <Alert severity="warning" sx={{ mb: 0 }}>
                {o?.ASSIGNMENT_STATUS === 'unassigned'
                  ? 'No shop could take this order automatically — pick one below.'
                  : 'This order was placed before fulfilment tracking started. Pick a shop below to assign it.'}
              </Alert>
            )}

            {/* Override / re-run */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
              <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
                <InputLabel id="fulfilment-store-label">Assign a shop</InputLabel>
                <Select
                  labelId="fulfilment-store-label"
                  label="Assign a shop"
                  value={selectedCandidate}
                  onChange={e => setSelectedCandidate(e.target.value)}
                  disabled={candidatesLoading || candidates.length === 0}
                >
                  {candidates.map(c => (
                    <MenuItem key={c.store_code} value={c.store_code}>
                      {candidateLabel(c)}
                    </MenuItem>
                  ))}
                  {!candidatesLoading && candidates.length === 0 && (
                    <MenuItem value="" disabled>
                      No shop holds these items
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="secondary"
                disabled={!selectedCandidate || assigning}
                onClick={() => handleAssign(selectedCandidate)}
              >
                {assigning ? <CircularProgress size={18} color="inherit" /> : 'Assign'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={assigning}
                onClick={() => handleAssign(null)}
              >
                Re-run auto pick
              </Button>
            </Box>
          </Box>

          {/* Items */}
          <Typography sx={{ mb: 1, fontSize: '0.8125rem', fontWeight: 600, color: color.ink[700] }}>
            Items
          </Typography>
          <Box sx={{ mb: 3 }}>
            <DataTable
              columns={itemColumns}
              rows={detail.items}
              rowKey="ITEMCODE"
              size="small"
              minWidth={560}
              emptyTitle="No items"
            />
          </Box>

          {/* Update status */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="order-status-label">Update Status</InputLabel>
              <Select
                labelId="order-status-label"
                label="Update Status"
                value={newStatus}
                onChange={e => setNewStatus(e.target.value)}
              >
                {ORDER_STATUSES.filter(s => s.value !== -1).map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleStatusSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : 'Save status'}
            </Button>
          </Box>
        </>
      )}
    </FormDialog>
  );
};

// ── Main Orders page ───────────────────────────────────────────
const Orders = () => {
  const { error: notifyError } = useFeedback();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(-1);
  const [inputVal, setInputVal] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/orders', {
        params: { status: statusFilter, page, pagesize: 20, search: search || undefined },
      });
      setOrders(res.data.data.orders);
      setTotalPages(res.data.data.pagination.totalPages);
    } catch {
      notifyError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, search, notifyError]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { const t = setTimeout(() => { setSearch(inputVal); setPage(1); }, 400); return () => clearTimeout(t); }, [inputVal]);

  const handleView = (orderId) => {
    setSelectedOrderId(orderId);
    setDialogOpen(true);
  };

  const columns = [
    {
      key: 'ORDERID',
      label: 'Order',
      width: 96,
      sx: { fontWeight: 600, color: color.ink[900] },
      render: (v) => `#${v}`,
    },
    {
      key: 'CUSTOMER_NAME',
      label: 'Customer',
      render: (_v, row) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: color.ink[900] }}>
            {row.CUSTOMER_NAME || '—'}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
            {row.EMAIL}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'order_date',
      label: 'Date',
      render: (v) => fmtDate(v),
    },
    {
      key: 'unique_items',
      label: 'Items',
      render: (v) => `${v} items`,
    },
    {
      key: 'AMOUNT_PAID',
      label: 'Amount',
      align: 'right',
      sx: { fontWeight: 600, color: color.ink[900] },
      render: (v) => fmt(v),
    },
    {
      key: 'PAYMENT_STATUS',
      label: 'Payment',
      render: (v) => (
        <StatusBadge
          label={v || 'pending'}
          tone={v === 'paid' ? 'success' : 'warning'}
        />
      ),
    },
    {
      key: 'ORDER_STATUS',
      label: 'Status',
      render: (v) => (
        <StatusBadge label={STATUS_LABEL[v] || 'Unknown'} tone={STATUS_TONE[v] || 'neutral'} />
      ),
    },
    {
      key: 'ASSIGNED_STORE_NAME',
      label: 'Assigned Store',
      render: (_v, row) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: color.ink[900] }}>
            {row.ASSIGNED_STORE_NAME ? `Hotspot ${row.ASSIGNED_STORE_NAME}` : (row.ASSIGNED_STORE_CODE ? `Branch #${row.ASSIGNED_STORE_CODE}` : 'Auto Routing')}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
            {row.DISTANCE_KM != null ? `${row.DISTANCE_KM} km · ` : ''}{row.ASSIGNMENT_MODE === 'manual' ? 'Store Pickup' : 'Delivery'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'center',
      width: 64,
      render: (_v, row) => (
        <Tooltip title="View details">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); handleView(row.ORDERID); }}
            aria-label="View order details"
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Commerce"
        title="Orders"
        subtitle={`Manage incoming orders · Page ${page} of ${totalPages}`}
        actions={
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={fetchOrders} disabled={loading} aria-label="Refresh orders">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Panel dense sx={{ mb: 2 }}>
        <Toolbar
          search={inputVal}
          onSearch={setInputVal}
          searchPlaceholder="Search by Order ID, customer or email..."
          onRefresh={fetchOrders}
          refreshing={loading}
        />
      </Panel>

      {/* Status filter */}
      <Box sx={{ mb: 2.5, borderBottom: `1px solid ${color.divider}` }}>
        <Tabs
          value={statusFilter}
          onChange={(_e, v) => { setStatusFilter(v); setPage(1); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {ORDER_STATUSES.map(s => (
            <Tab key={s.value} value={s.value} label={s.label} />
          ))}
        </Tabs>
      </Box>

      <DataTable
        columns={columns}
        rows={orders}
        rowKey="ORDERID"
        loading={loading}
        minWidth={900}
        emptyTitle="No orders found"
        emptyDescription="Try a different status filter."
        onRowClick={(row) => handleView(row.ORDERID)}
      />

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, v) => setPage(v)}
            color="primary"
          />
        </Stack>
      )}

      <OrderDetailDialog
        orderId={selectedOrderId}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onChanged={fetchOrders}
      />
    </Box>
  );
};

export default Orders;
